import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    inject,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    ViewEncapsulation,
} from "@angular/core";
import { S25DropdownAbstract } from "../s25.dropdown.abstract";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { BpeVarsConst } from "../../bpe/s25.bpe.const";
import { Doc } from "../../system-settings/document-management/s25.document.const";
import { DropDownItem } from "../../../pojo/DropDownItem";

@TypeManagerDecorator("s25-bpe-vars-dropdown")
@Component({
    selector: "s25-bpe-vars-dropdown",
    template: `
        @if (items) {
            <s25-generic-dropdown
                [items]="items"
                [value]="value()"
                [onSelect]="onSelect"
                (valueChange)="onValueChange($event)"
                [searchEnabled]="true"
                [ariaLabel]="ariaLabel"
                [placeholder]="placeholder"
                [hasNoneItem]="hasNoneItem"
                [scrollIntoView]="scrollIntoView"
                [readOnly]="readOnly"
            />
        }
    `,
    styles: ``,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class S25BpeVarsDropdownComponent extends S25DropdownAbstract implements OnInit, OnChanges {
    private readonly destroyRef = inject(DestroyRef);

    @Input() type?: keyof typeof BpeVarsConst.Items;
    @Input() types?: (keyof typeof BpeVarsConst.Items)[];
    @Input() placeholder: string;
    @Input() documentType: Doc.TypeId;
    @Input() itemFilter: (items: DropDownItem[]) => DropDownItem[] | Promise<DropDownItem[]>;
    @Input() dataTypes?: string[];

    constructor(private changeDetector: ChangeDetectorRef) {
        super();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.type || changes.documentType || changes.types || changes.itemFilter || changes.dataTypes) {
            this.setItems();
        }
    }

    ngOnInit() {
        this.setItems();
    }

    async setItems() {
        if (this.destroyRef.destroyed) return;
        const types = this.types || (this.type ? [this.type] : ["event"]);
        let items = types.flatMap((type) => (BpeVarsConst.Items[type] as DropDownItem[]) || []);
        if (this.itemFilter) {
            items = await this.itemFilter(items);
            if (this.destroyRef.destroyed) return;
        }
        if (this.documentType) {
            items = items.filter((item) => !item.documentTypes || item.documentTypes.includes(this.documentType));
        }
        if (this.dataTypes) {
            items = items.filter((item) => this.dataTypes.includes(item.type));
        }

        const selected = this.value();
        if (!items.some((item) => item === selected)) this.onValueChange(undefined);

        this.items = items;
        this.changeDetector.detectChanges();
    }
}

export type VariableWithOptions = {
    val?: string;
    itemId?: string | number;
    txt?: string;
    options?: VariableOption[];
    hasFormat?: boolean;
    vars?: { var: string; options: Record<string, boolean> }[];
    noPre?: boolean;
    type?: string;
    reportBuilder?: { groups: string[] };
};

export type VariableOption = {
    type: VariableOptionType;
    id: string;
    label?: string;
    substitute?: string;
    prop?: string;
    hidden?: boolean;
    data?: any;
    documentTypes?: Doc.TypeId[];
    options?: VariableWithOptions[];
    default?: any;
};

export type VariableOptionType =
    | "checkbox"
    | "toggle"
    | "dropdown"
    | "roleDropdown"
    | "custom"
    | "searchCriteriaItem"
    | "searchCriteriaItems"
    | "number";
